import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BookLendResponse,
  BooksApiResponse,
  BooksState,
  CreateBooksApiResponse,
  DeleteBookResponseBody,
  GenreResponse,
  IBulkUploadResponse,
  UpdateBookApiResponse,
} from '../../interface/libraryManagement';
import {
  bookBulkUpload,
  bookLendin,
  bookLendOut,
  createBook,
  deleteBook,
  getAllBooks,
  getAllGenreTypes,
  getBookTemplate,
  updateBook,
} from '../action/libraryManagement.action';

export const initialState: BooksState = {
  books: [],
  total: 0,
  page: 1,
  pageSize: 5,
  genres: [],
  message: '',
  isLoading: false,
  error: null,
  bulkUploadSummary: null,
};

const bookSlice = createSlice({
  name: 'books',
  initialState,
  reducers: {
    clearBulkUploadSummary: (state) => {
      state.bulkUploadSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllBooks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.books = initialState.books;
        state.total = initialState.total;
        state.page = initialState.page;
        state.pageSize = initialState.pageSize;
      })
      .addCase(getAllBooks.fulfilled, (state, action: PayloadAction<BooksApiResponse>) => {
        state.isLoading = false;
        state.books = action.payload.data?.books;
        state.total = action.payload.data?.total ?? 0;
        state.page = action.payload.data?.page ?? 1;
        state.pageSize = action.payload.data?.pageSize ?? 5;
        state.error = null;
      })
      .addCase(getAllBooks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.books = initialState.books;
        state.total = initialState.total;
        state.page = initialState.page;
        state.pageSize = initialState.pageSize;
      })
      .addCase(getAllGenreTypes.pending, (state) => {
        state.error = null;
        state.genres = [];
      })
      .addCase(getAllGenreTypes.fulfilled, (state, action: PayloadAction<GenreResponse>) => {
        state.genres = action.payload.data.genres ?? [];
        state.error = null;
      })
      .addCase(getAllGenreTypes.rejected, (state, action) => {
        state.error = action.payload;
        state.genres = [];
      })
      .addCase(createBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBook.fulfilled, (state, _action: PayloadAction<CreateBooksApiResponse>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBook.fulfilled, (state, _action: PayloadAction<UpdateBookApiResponse>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteBook.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBook.fulfilled, (state, _action: PayloadAction<DeleteBookResponseBody>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteBook.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(bookLendin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bookLendin.fulfilled, (state, _action: PayloadAction<BookLendResponse>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(bookLendin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(bookLendOut.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bookLendOut.fulfilled, (state, _action: PayloadAction<BookLendResponse>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(bookLendOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getBookTemplate.pending, (state) => {
        state.error = null;
      })
      .addCase(getBookTemplate.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(getBookTemplate.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(bookBulkUpload.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.bulkUploadSummary = null;
      })
      .addCase(bookBulkUpload.fulfilled, (state, action: PayloadAction<IBulkUploadResponse>) => {
        state.isLoading = false;
        state.bulkUploadSummary = action.payload.data;
        state.error = null;
      })
      .addCase(bookBulkUpload.rejected, (state, action) => {
        state.isLoading = false;
        state.bulkUploadSummary = null;
        state.error = action.payload;
      });
  },
});

export const { clearBulkUploadSummary } = bookSlice.actions;

export default bookSlice.reducer;
